import { useMemo, useState } from "react";
import { Redirect, Switch } from "react-router-dom";
import { Button, Form, Modal } from "react-bootstrap";
import { Field, Form as FormikForm, Formik, useFormikContext } from "formik";
import * as Yup from "yup";
import { Card, CardBody, CardHeader, CardHeaderToolbar } from "../../../../_metronic/_partials/controls";
import { ContentRoute } from "../../../../_metronic/layout";
import { FilterGroup } from "../../Common/components/FilterGroup";
import TextInput from "../../Common/components/text-input";
import { SubHeader } from "../../Common/layout/SubHeader";
import { Permission } from "../../Common/layout/Permission";
import { EditIcon, TrashIcon, ViewIcon } from "../../../util/Icons";
import { Permissions, PermissionType } from "../../../util/Permissions";
import { formatAmount, formatDate, formatStatus, formatUsername, ucFirst } from "../../../util/Helpers";
import {
    useCreateCommercialPaperMutation,
    useDeleteCommercialPaperMutation,
    useGenerateCommercialPaperSubscriptionsReportCsvMutation,
    useGenerateCommercialPaperSubscriptionsReportXlsxMutation,
    useGenerateCommercialPapersReportCsvMutation,
    useGenerateCommercialPapersReportXlsxMutation,
    useGetCommercialPapersQuery,
    useGetCommercialPaperSubscriptionsQuery,
    useGetUserProfileQuery,
    useUpdateCommercialPaperMutation,
} from "../../../../redux/apiSlice";
import cogoToast from "cogo-toast";

const normalizeArray = (payload) => {
    if (Array.isArray(payload)) return payload;
    if (Array.isArray(payload?.data)) return payload.data;
    if (Array.isArray(payload?.items)) return payload.items;
    if (Array.isArray(payload?.results)) return payload.results;
    return [];
};

const sanitizeValue = (value) => typeof value === "string" ? value.replace(/`/g, "").trim() : value;
const sanitizeOptionalString = (value) => {
    if (value === null || value === undefined) return undefined;
    const sanitized = sanitizeValue(value);
    if (typeof sanitized === "string" && sanitized.trim() === "") return undefined;
    return sanitized;
};
const toOptionalNumber = (value) => {
    if (value === "" || value === null || value === undefined) return undefined;
    const numeric = Number(value);
    return Number.isFinite(numeric) ? numeric : undefined;
};
const toDateValue = (value) => {
    if (!value) return null;
    const date = new Date(value);
    return Number.isNaN(date.getTime()) ? null : date;
};
const toDateTimeLocal = (value) => {
    if (!value) return "";
    const date = new Date(value);
    if (Number.isNaN(date.getTime())) return "";
    const pad = (entry) => `${entry}`.padStart(2, "0");
    return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}T${pad(date.getHours())}:${pad(date.getMinutes())}`;
};
const toIsoOrUndefined = (value) => value ? new Date(value).toISOString() : undefined;

const getCommercialPaperId = (row) => row?.id || row?.cpId || row?.referenceId || row?.name;
const getCommercialPaperName = (row) => row?.name || row?.cpName || row?.commercialPaperName || row?.title || "-";
const getCommercialPaperIssuer = (row) => row?.issuer || row?.issuerName || row?.company || row?.provider || "-";

const CommercialPaperFilterOptions = ({ commercialPapers, title = "All CPs", limit = 10 }) => {
    const [query, setQuery] = useState("");
    const [showAll, setShowAll] = useState(false);

    const filtered = useMemo(() => {
        const q = (query || "").trim().toLowerCase();
        if (!q) return commercialPapers;
        return commercialPapers.filter((cp) => {
            const name = getCommercialPaperName(cp) || "";
            const issuer = getCommercialPaperIssuer(cp) || "";
            const id = getCommercialPaperId(cp) || "";
            return `${name} ${issuer} ${id}`.toLowerCase().includes(q);
        });
    }, [commercialPapers, query]);

    const visible = useMemo(() => (showAll ? filtered : filtered.slice(0, limit)), [filtered, showAll, limit]);

    return (
        <div className="d-flex flex-column" style={{ maxHeight: 360 }}>
            <div className="p-3 border-bottom">
                <input
                    type="text"
                    className="form-control form-control-sm"
                    value={query}
                    onChange={(e) => setQuery(e.target.value)}
                    placeholder="Search CP..."
                />
            </div>
            <div
                style={{ maxHeight: 260, overflowY: "auto" }}
                onWheel={(e) => e.stopPropagation()}
                onTouchMove={(e) => e.stopPropagation()}
            >
                <FilterGroup.Option value="">{title}</FilterGroup.Option>
                {visible.map((cp) => (
                    <FilterGroup.Option key={getCommercialPaperId(cp)} value={getCommercialPaperId(cp)}>
                        <div className="d-flex flex-column">
                            <div className="font-weight-bolder">{getCommercialPaperName(cp)}</div>
                            <div className="text-muted font-size-sm">{getCommercialPaperIssuer(cp) || "-"}</div>
                        </div>
                    </FilterGroup.Option>
                ))}
                {!visible.length && (
                    <div className="p-3 text-center text-muted">No matches</div>
                )}
            </div>
            {filtered.length > limit ? (
                <div className="p-2 border-top">
                    <button
                        type="button"
                        className="btn btn-light-primary btn-sm btn-block"
                        onClick={() => setShowAll((v) => !v)}
                    >
                        {showAll ? `Show top ${limit}` : `Show all (${filtered.length})`}
                    </button>
                </div>
            ) : null}
        </div>
    );
};
const getCommercialPaperStatus = (row) => row?.status || row?.cpStatus || "-";
const getCommercialPaperTenor = (row) => row?.tenor || row?.tenorInDays || row?.duration || "-";
const getCommercialPaperYield = (row) => row?.yield || row?.yieldRate || row?.interestRate || row?.returnRate;
const getSubscriptionWindowStart = (row) => row?.subscriptionWindowStart || row?.subscriptionStartDate || row?.offerStartDate || row?.startDate;
const getCommercialPaperMaturityDate = (row) => row?.maturityDate || row?.maturesAt || row?.settlementDate || row?.endDate;
const getCommercialPaperUpdatedAt = (row) => row?.updated || row?.updatedAt;
const getCommercialPaperCurrency = (row) => row?.currency || row?.baseCurrency || row?.subscriptionCurrency || "NGN";
const getCommercialPaperLogo = (row) => sanitizeValue(row?.meta?.logo || row?.logo);
const getCommercialPaperTotalSubscribed = (row) => row?.meta?.totalSubscribedAmount ?? row?.totalSubscribedAmount ?? row?.totalAmountSubscribed ?? row?.amountSubscribed ?? row?.totalSubscribed ?? 0;
const getCommercialPaperCreditRating = (row) => row?.meta?.creditRating ?? row?.creditRating;
const getCommercialPaperDaysLeft = (row) => row?.meta?.daysLeft;
const getCommercialPaperDaysAccrued = (row) => row?.meta?.daysAccrued;
const getCommercialPaperSycamoreCreatedAt = (row) => row?.meta?.sycamoreCreatedAt;
const getCommercialPaperSycamoreUpdatedAt = (row) => row?.meta?.sycamoreUpdatedAt;
const getCommercialPaperProviderRef = (row) => row?.providerRef ?? row?.meta?.providerRef;
const getCommercialPaperProductId = (row) => row?.productId ?? row?.meta?.productId;
const getCommercialPaperMinAmount = (row) => row?.minAmount;
const getCommercialPaperMaxAmount = (row) => row?.maxAmount;
const getCommercialPaperDescription = (row) => row?.description;
const stripHtml = (value) => {
    const input = sanitizeValue(value);
    if (!input) return "";
    try {
        const doc = new DOMParser().parseFromString(input, "text/html");
        return doc.body.textContent || "";
    } catch {
        return input;
    }
};
// CP name for a subscription. Read it from the subscription itself first
// (its meta object holds the CP details at subscription time), because
// older CPs are no longer returned by Sycamore, so matching against the
// current CP list leaves those rows blank. The list match is only a fallback.
const getSubscriptionCommercialPaperName = (subscription, relatedCommercialPaper) => {
    const meta = subscription?.meta || {};
    const fromSubscription =
        meta.cpName ||
        meta.commercialPaperName ||
        meta.commercialPaper?.name ||
        meta.commercialPaper?.cpName ||
        meta.cp?.name ||
        meta.purchaseSummary?.cpName ||
        meta.purchaseSummary?.commercialPaperName ||
        subscription?.commercialPaperName ||
        subscription?.cpName;
    if (fromSubscription) return fromSubscription;
    return getCommercialPaperName(relatedCommercialPaper || {});
};
const getSubscriptionCommercialPaperId = (row) => row?.commercialPaperId || row?.cpId || row?.paperId || row?.id;
const getSubscriptionAmount = (row) => row?.subscriptionAmount || row?.amount || row?.amountSubscribed || row?.principal || 0;
const getSubscriptionDate = (row) => row?.subscriptionDate || row?.created || row?.createdAt || row?.updated;
const getSubscriptionStatus = (row) => row?.status || row?.subscriptionStatus || "-";
const getSubscriptionMaturityDate = (row) => row?.maturityDate || row?.maturesAt || row?.commercialPaperMaturityDate;
const getSubscriptionUserGroup = (row) => row?.userGroup || row?.groupId || row?.group || row?.tenant;
const getCommercialPaperUserGroup = (row) => row?.userGroup || row?.groupId || row?.group || row?.tenant;
const getSubscriptionExpectedReturns = (row) => row?.expectedReturns ?? row?.meta?.subscriptionAmounts?.expectedReturns ?? row?.meta?.purchaseSummary?.expectedReturns;
const getSubscriptionFees = (row) => row?.fees ?? row?.meta?.subscriptionAmounts?.fees ?? row?.meta?.purchaseSummary?.transactionFees;
const getSubscriptionInterestRate = (row) => row?.interestRate ?? row?.meta?.subscriptionAmounts?.interestRate ?? row?.meta?.purchaseSummary?.interestRate;
const getSubscriptionValueAtMaturity = (row) => row?.valueAtMaturity ?? row?.meta?.subscriptionAmounts?.valueAtMaturity ?? row?.meta?.purchaseSummary?.valueAtMaturity;
const getSubscriptionTransactionRef = (row) => row?.transactionRef || row?.meta?.paymentRefId || row?.meta?.purchaseSummary?.paymentRefId || "-";
const getSubscriptionPaymentMethod = (row) => row?.paymentMethod || row?.meta?.paymentMethod || "-";
const getSubscriptionPayoutDate = (row) => row?.payoutDate || row?.meta?.purchaseSummary?.payoutDate;
const getSubscriptionDaysLeft = (row) => row?.meta?.daysLeft ?? row?.meta?.purchaseSummary?.daysLeft;
const getSubscriptionDaysAccrued = (row) => row?.meta?.daysAccrued ?? row?.meta?.purchaseSummary?.daysAccrued;

const renderCommercialPaperStatus = (status) => {
    const normalized = status?.toLowerCase();
    const color = normalized === "active" ? "primary" : normalized === "matured" ? "success" : normalized === "inactive" ? "warning" : "muted";
    return (
        <span className={`label label-lg label-light-${color} label-inline`}>
            {ucFirst(status || "unknown")}
        </span>
    );
};

const formatMaybeAmount = (currency, value) => {
    const numericValue = Number(value);
    if (!Number.isFinite(numericValue)) {
        return value || "-";
    }
    return formatAmount(currency || "NGN", numericValue);
};

const buildSupportDocumentArray = (filename, originalName) => {
    const cleanFileName = sanitizeValue(filename);
    const cleanOriginalName = sanitizeValue(originalName);
    if (!cleanFileName && !cleanOriginalName) {
        return [];
    }
    return [{ filename: cleanFileName || "", originalName: cleanOriginalName || "" }];
};

const buildSupportDocumentsPayload = (values, { includeEmpty = false } = {}) => {
    const ratingReport = buildSupportDocumentArray(values.ratingReportFilename, values.ratingReportOriginalName);
    const commitmentForm = buildSupportDocumentArray(values.commitmentFormFilename, values.commitmentFormOriginalName);
    const pricingSupplement = buildSupportDocumentArray(values.pricingSupplementFilename, values.pricingSupplementOriginalName);
    const programmeMemorandum = buildSupportDocumentArray(values.programmeMemorandumFilename, values.programmeMemorandumOriginalName);
    const auditedFinancialStatement = buildSupportDocumentArray(values.auditedFinancialStatementFilename, values.auditedFinancialStatementOriginalName);

    const payload = {};
    if (includeEmpty || ratingReport.length) payload.ratingReport = ratingReport;
    if (includeEmpty || commitmentForm.length) payload.commitmentForm = commitmentForm;
    if (includeEmpty || pricingSupplement.length) payload.pricingSupplement = pricingSupplement;
    if (includeEmpty || programmeMemorandum.length) payload.programmeMemorandum = programmeMemorandum;
    if (includeEmpty || auditedFinancialStatement.length) payload.auditedFinancialStatement = auditedFinancialStatement;

    return Object.keys(payload).length ? payload : undefined;
};

const buildCommercialPaperPayload = (values, { mode } = { mode: "create" }) => {
    const base = {
        name: sanitizeOptionalString(values.name),
        issuer: sanitizeOptionalString(values.issuer),
        description: sanitizeOptionalString(values.description),
        tenor: toOptionalNumber(values.tenor),
        duration: toOptionalNumber(values.duration),
        interestRate: toOptionalNumber(values.interestRate),
        yield: toOptionalNumber(values.yield),
        minAmount: toOptionalNumber(values.minAmount),
        maxAmount: toOptionalNumber(values.maxAmount),
        programmeSize: toOptionalNumber(values.programmeSize),
        targetSize: toOptionalNumber(values.targetSize),
        series: sanitizeOptionalString(values.series),
        subscriptionStartDate: toIsoOrUndefined(values.subscriptionStartDate),
        subscriptionEndDate: toIsoOrUndefined(values.subscriptionEndDate),
        settlementDate: toIsoOrUndefined(values.settlementDate),
        status: sanitizeOptionalString(values.status),
        currency: sanitizeOptionalString(values.currency),
        country: sanitizeOptionalString(values.country),
        purpose: sanitizeOptionalString(values.purpose),
        whtRate: toOptionalNumber(values.whtRate),
        issuingHouse: sanitizeOptionalString(values.issuingHouse),
        maximumDuration: toOptionalNumber(values.maximumDuration),
        minimumDuration: toOptionalNumber(values.minimumDuration),
        supportDocuments: buildSupportDocumentsPayload(values, { includeEmpty: mode === "create" }),
    };

    if (mode === "create") {
        return base;
    }

    return Object.keys(base).reduce((acc, key) => {
        const value = base[key];
        if (value !== undefined) acc[key] = value;
        return acc;
    }, {});
};

const commercialPaperToFormValues = (commercialPaper) => {
    const supportDocs = commercialPaper?.supportDocuments || commercialPaper?.documents || commercialPaper?.support_docs || {};

    return {
        name: commercialPaper?.name || commercialPaper?.title || "",
        issuer: commercialPaper?.issuer || commercialPaper?.issuerName || commercialPaper?.issuer_name || "",
        description: commercialPaper?.description || commercialPaper?.details || "",
        tenor: commercialPaper?.tenor ?? commercialPaper?.tenorInDays ?? commercialPaper?.tenor_in_days ?? commercialPaper?.durationInDays ?? "",
        duration: commercialPaper?.duration ?? commercialPaper?.durationInDays ?? commercialPaper?.duration_in_days ?? "",
        interestRate: commercialPaper?.interestRate ?? commercialPaper?.interest_rate ?? "",
        yield: commercialPaper?.yield ?? commercialPaper?.yieldRate ?? commercialPaper?.yield_rate ?? commercialPaper?.returnRate ?? "",
        minAmount: commercialPaper?.minAmount ?? commercialPaper?.min_amount ?? "",
        maxAmount: commercialPaper?.maxAmount ?? commercialPaper?.max_amount ?? "",
        programmeSize: commercialPaper?.programmeSize ?? commercialPaper?.programSize ?? commercialPaper?.programme_size ?? commercialPaper?.program_size ?? "",
        targetSize: commercialPaper?.targetSize ?? commercialPaper?.target_size ?? "",
        series: commercialPaper?.series || commercialPaper?.seriesName || commercialPaper?.series_name || "",
        subscriptionStartDate: toDateTimeLocal(
            commercialPaper?.subscriptionStartDate || commercialPaper?.subscriptionWindowStart || commercialPaper?.offerStartDate || commercialPaper?.startDate
        ),
        subscriptionEndDate: toDateTimeLocal(
            commercialPaper?.subscriptionEndDate || commercialPaper?.subscriptionWindowEnd || commercialPaper?.offerEndDate || commercialPaper?.endDate
        ),
        settlementDate: toDateTimeLocal(commercialPaper?.settlementDate || commercialPaper?.settlement_date),
        status: commercialPaper?.status || commercialPaper?.state || "active",
        interestPaymentType: commercialPaper?.interestPaymentType || commercialPaper?.interest_payment_type || "maturity",
        currency: commercialPaper?.currency || commercialPaper?.currencyCode || commercialPaper?.currency_code || "NGN",
        logo: sanitizeValue(commercialPaper?.logo || commercialPaper?.logoUrl || commercialPaper?.logo_url) || "",
        country: commercialPaper?.country || commercialPaper?.countryCode || commercialPaper?.country_code || "Nigeria",
        purpose: commercialPaper?.purpose || commercialPaper?.usage || "",
        whtRate: commercialPaper?.whtRate ?? commercialPaper?.wht_rate ?? "",
        creditRating: commercialPaper?.creditRating || commercialPaper?.credit_rating || "",
        issuingHouse: commercialPaper?.issuingHouse || commercialPaper?.issuing_house || "",
        maximumDuration: commercialPaper?.maximumDuration ?? commercialPaper?.maxDuration ?? commercialPaper?.maximum_duration ?? "",
        minimumDuration: commercialPaper?.minimumDuration ?? commercialPaper?.minDuration ?? commercialPaper?.minimum_duration ?? "",
        ratingReportFilename: sanitizeValue(supportDocs?.ratingReport?.[0]?.filename) || "",
        ratingReportOriginalName: supportDocs?.ratingReport?.[0]?.originalName || "",
        ratingReportFile: null,
        commitmentFormFilename: sanitizeValue(supportDocs?.commitmentForm?.[0]?.filename) || "",
        commitmentFormOriginalName: supportDocs?.commitmentForm?.[0]?.originalName || "",
        commitmentFormFile: null,
        pricingSupplementFilename: sanitizeValue(supportDocs?.pricingSupplement?.[0]?.filename) || "",
        pricingSupplementOriginalName: supportDocs?.pricingSupplement?.[0]?.originalName || "",
        pricingSupplementFile: null,
        programmeMemorandumFilename: sanitizeValue(supportDocs?.programmeMemorandum?.[0]?.filename) || "",
        programmeMemorandumOriginalName: supportDocs?.programmeMemorandum?.[0]?.originalName || "",
        programmeMemorandumFile: null,
        auditedFinancialStatementFilename: sanitizeValue(supportDocs?.auditedFinancialStatement?.[0]?.filename) || "",
        auditedFinancialStatementOriginalName: supportDocs?.auditedFinancialStatement?.[0]?.originalName || "",
        auditedFinancialStatementFile: null,
    };
};

const createCommercialPaperValidationSchema = Yup.object({
    name: Yup.string().required("Required"),
    issuer: Yup.string().required("Required"),
    description: Yup.string().required("Required"),
    tenor: Yup.number().required("Required").min(1),
    duration: Yup.number().required("Required").min(1),
    interestRate: Yup.number().required("Required").min(0),
    yield: Yup.number().required("Required").min(0),
    minAmount: Yup.number().required("Required").min(0),
    maxAmount: Yup.number().required("Required").min(0),
    programmeSize: Yup.number().required("Required").min(0),
    targetSize: Yup.number().required("Required").min(0),
    series: Yup.string().required("Required"),
    subscriptionStartDate: Yup.string().required("Required"),
    subscriptionEndDate: Yup.string().required("Required"),
    settlementDate: Yup.string().required("Required"),
    status: Yup.string().required("Required"),
    currency: Yup.string().required("Required"),
    country: Yup.string().required("Required"),
    purpose: Yup.string().required("Required"),
    whtRate: Yup.number().required("Required").min(0),
    issuingHouse: Yup.string().required("Required"),
    maximumDuration: Yup.number().required("Required").min(1),
    minimumDuration: Yup.number().required("Required").min(1),
});

const updateCommercialPaperValidationSchema = Yup.object({
    name: Yup.string().transform((value) => (value === "" ? undefined : value)).notRequired(),
    issuer: Yup.string().transform((value) => (value === "" ? undefined : value)).notRequired(),
    description: Yup.string().transform((value) => (value === "" ? undefined : value)).notRequired(),
    tenor: Yup.number().transform((value, originalValue) => (originalValue === "" ? undefined : value)).min(1).notRequired(),
    duration: Yup.number().transform((value, originalValue) => (originalValue === "" ? undefined : value)).min(1).notRequired(),
    interestRate: Yup.number().transform((value, originalValue) => (originalValue === "" ? undefined : value)).min(0).notRequired(),
    yield: Yup.number().transform((value, originalValue) => (originalValue === "" ? undefined : value)).min(0).notRequired(),
    minAmount: Yup.number().transform((value, originalValue) => (originalValue === "" ? undefined : value)).min(0).notRequired(),
    maxAmount: Yup.number().transform((value, originalValue) => (originalValue === "" ? undefined : value)).min(0).notRequired(),
    programmeSize: Yup.number().transform((value, originalValue) => (originalValue === "" ? undefined : value)).min(0).notRequired(),
    targetSize: Yup.number().transform((value, originalValue) => (originalValue === "" ? undefined : value)).min(0).notRequired(),
    series: Yup.string().transform((value) => (value === "" ? undefined : value)).notRequired(),
    subscriptionStartDate: Yup.string().transform((value) => (value === "" ? undefined : value)).notRequired(),
    subscriptionEndDate: Yup.string().transform((value) => (value === "" ? undefined : value)).notRequired(),
    settlementDate: Yup.string().transform((value) => (value === "" ? undefined : value)).notRequired(),
    status: Yup.string().transform((value) => (value === "" ? undefined : value)).notRequired(),
    currency: Yup.string().transform((value) => (value === "" ? undefined : value)).notRequired(),
    country: Yup.string().transform((value) => (value === "" ? undefined : value)).notRequired(),
    purpose: Yup.string().transform((value) => (value === "" ? undefined : value)).notRequired(),
    whtRate: Yup.number().transform((value, originalValue) => (originalValue === "" ? undefined : value)).min(0).notRequired(),
    issuingHouse: Yup.string().transform((value) => (value === "" ? undefined : value)).notRequired(),
    maximumDuration: Yup.number().transform((value, originalValue) => (originalValue === "" ? undefined : value)).min(1).notRequired(),
    minimumDuration: Yup.number().transform((value, originalValue) => (originalValue === "" ? undefined : value)).min(1).notRequired(),
});

const SupportDocumentFields = ({ prefix, label }) => {
    const { values, setFieldValue } = useFormikContext();
    const selectedFile = values[`${prefix}File`];
    const currentFileName = values[`${prefix}Filename`];
    const currentOriginalName = values[`${prefix}OriginalName`];
    const inputId = `commercial-paper-${prefix}-upload`;

    return (
        <div className="border rounded p-4 mb-4">
            <div className="font-weight-bolder mb-3">{label}</div>
            <div className="row">
                <div className="form-group col">
                    <label>Upload File</label>
                    <input
                        id={inputId}
                        type="file"
                        className="d-none"
                        accept=".pdf,.csv,.xls,.xlsx"
                        onChange={(event) => {
                            const file = event.currentTarget.files?.[0] || null;
                            setFieldValue(`${prefix}File`, file);
                            setFieldValue(`${prefix}OriginalName`, file?.name || currentOriginalName || "");
                            setFieldValue(`${prefix}Filename`, file?.name || "");
                        }}
                    />
                    <div className="d-flex align-items-center flex-wrap">
                        <label htmlFor={inputId} className="btn btn-light-primary mb-0 mr-3">
                            {selectedFile || currentOriginalName ? "Replace Document" : "Choose Document"}
                        </label>
                        <span className="text-muted font-size-sm">
                            {selectedFile?.name || currentOriginalName || "No file selected"}
                        </span>
                    </div>
                    <small className="form-text text-muted">
                        Accepted formats: PDF, CSV, XLS, XLSX
                    </small>
                    {!selectedFile && currentFileName && /^https?:\/\//.test(currentFileName) && (
                        <a href={currentFileName} target="_blank" rel="noreferrer" className="d-inline-block mt-2">
                            View current file
                        </a>
                    )}
                </div>
                <TextInput label="Display Name" name={`${prefix}OriginalName`} type="text" />
            </div>
        </div>
    );
};

const CommercialPaperFormModal = ({ show, onHide, initialCommercialPaper }) => {
    const isEdit = !!initialCommercialPaper;
    const [createCommercialPaper, { isLoading: isCreatingCommercialPaper }] = useCreateCommercialPaperMutation();
    const [updateCommercialPaper, { isLoading: isUpdatingCommercialPaper }] = useUpdateCommercialPaperMutation();
    const isSubmitting = isCreatingCommercialPaper || isUpdatingCommercialPaper;

    return (
        <Modal show={show} onHide={onHide} size="xl" centered backdrop="static">
            <Formik
                enableReinitialize
                initialValues={commercialPaperToFormValues(initialCommercialPaper)}
                validationSchema={isEdit ? updateCommercialPaperValidationSchema : createCommercialPaperValidationSchema}
                onSubmit={(values, { setSubmitting, resetForm }) => {
                    const payload = buildCommercialPaperPayload(values, { mode: isEdit ? "update" : "create" });
                    const action = isEdit
                        ? updateCommercialPaper({ cpId: getCommercialPaperId(initialCommercialPaper), ...payload })
                        : createCommercialPaper(payload);
                    action.unwrap()
                        .then(() => {
                            cogoToast.success(`Commercial paper ${isEdit ? "updated" : "created"} successfully`);
                            if (!isEdit) {
                                resetForm();
                            }
                            onHide();
                        })
                        .catch(() => {})
                        .finally(() => setSubmitting(false));
                }}
            >
                {({ isValid }) => (
                    <FormikForm>
                        <Modal.Header closeButton>
                            <Modal.Title id="contained-modal-title-vcenter">
                                {isEdit ? "Update Commercial Paper" : "Create Commercial Paper"}
                            </Modal.Title>
                        </Modal.Header>
                        <Modal.Body style={{ maxHeight: "75vh", overflowY: "auto" }}>
                            <div className="row">
                                <TextInput label="Name" name="name" type="text" />
                                <TextInput label="Issuer" name="issuer" type="text" />
                            </div>
                            <div className="row">
                                <div className="form-group col">
                                    <label>Description</label>
                                    <Field as="textarea" name="description" className="form-control" rows="3" />
                                </div>
                            </div>
                            <div className="row">
                                <TextInput label="Tenor" name="tenor" type="number" />
                                <TextInput label="Duration" name="duration" type="number" />
                                <TextInput label="Interest Rate" name="interestRate" type="number" step="any" />
                                <TextInput label="Yield" name="yield" type="number" step="any" />
                            </div>
                            <div className="row">
                                <TextInput label="Min Amount" name="minAmount" type="number" step="any" />
                                <TextInput label="Max Amount" name="maxAmount" type="number" step="any" />
                                <TextInput label="Programme Size" name="programmeSize" type="number" step="any" />
                                <TextInput label="Target Size" name="targetSize" type="number" step="any" />
                            </div>
                            <div className="row">
                                <TextInput label="Series" name="series" type="text" />
                                <div className="form-group col">
                                    <label>Status</label>
                                    <Field as="select" name="status" className="form-control">
                                        <option value="active">Active</option>
                                        <option value="inactive">Inactive</option>
                                        <option value="matured">Matured</option>
                                    </Field>
                                </div>
                                <div className="form-group col">
                                    <label>Currency</label>
                                    <Field as="select" name="currency" className="form-control">
                                        <option value="NGN">NGN</option>
                                        <option value="USD">USD</option>
                                    </Field>
                                </div>
                            </div>
                            <div className="row">
                                <TextInput label="Subscription Start" name="subscriptionStartDate" type="datetime-local" />
                                <TextInput label="Subscription End" name="subscriptionEndDate" type="datetime-local" />
                                <TextInput label="Settlement Date" name="settlementDate" type="datetime-local" />
                            </div>
                            <div className="row">
                                <TextInput label="Country" name="country" type="text" />
                            </div>
                            <div className="row">
                                <div className="form-group col">
                                    <label>Purpose</label>
                                    <Field as="textarea" name="purpose" className="form-control" rows="3" />
                                </div>
                            </div>
                            <div className="row">
                                <TextInput label="Withholding tax" name="whtRate" type="number" step="any" />
                                <TextInput label="Issuing House" name="issuingHouse" type="text" />
                            </div>
                            <div className="row">
                                <TextInput label="Maximum Duration" name="maximumDuration" type="number" />
                                <TextInput label="Minimum Duration" name="minimumDuration" type="number" />
                            </div>
                            <SupportDocumentFields prefix="ratingReport" label="Rating Report" />
                            <SupportDocumentFields prefix="commitmentForm" label="Commitment Form" />
                            <SupportDocumentFields prefix="pricingSupplement" label="Pricing Supplement" />
                            <SupportDocumentFields prefix="programmeMemorandum" label="Programme Memorandum" />
                            <SupportDocumentFields prefix="auditedFinancialStatement" label="Audited Financial Statement" />
                        </Modal.Body>
                        <Modal.Footer>
                            <Button variant="light" onClick={onHide} disabled={isSubmitting}>
                                Cancel
                            </Button>
                            <Button type="submit" variant="primary" disabled={!isValid || isSubmitting}>
                                {isSubmitting ? "Saving..." : isEdit ? "Update Commercial Paper" : "Create Commercial Paper"}
                            </Button>
                        </Modal.Footer>
                    </FormikForm>
                )}
            </Formik>
        </Modal>
    );
};

const SubscriptionDetailsModal = ({ subscription, commercialPaper, onHide }) => {
    const { data: userProfileResponse, isFetching: isFetchingUserProfile } = useGetUserProfileQuery(subscription?.userId, {
        skip: !subscription?.userId,
    });
    const userProfile = userProfileResponse?.data || userProfileResponse;
    const resolvedName = userProfile?.username || userProfile?.name || userProfile?.firstName
        ? `${userProfile?.firstName || ""} ${userProfile?.lastName || ""}`.trim() || userProfile?.username || userProfile?.name
        : subscription?.name || subscription?.username;

    return (
        <Modal show={!!subscription} onHide={onHide} centered size="lg">
            <Modal.Header closeButton>
                <Modal.Title id="contained-modal-title-vcenter">Commercial Subscription Details</Modal.Title>
            </Modal.Header>
            <Modal.Body>
                {subscription && (
                    <div className="row">
                        <div className="col-md-6 mb-4">
                            <div className="font-weight-bold text-muted mb-1">Commercial Paper</div>
                            <div>{getSubscriptionCommercialPaperName(subscription, commercialPaper)}</div>
                        </div>
                        <div className="col-md-6 mb-4">
                            <div className="font-weight-bold text-muted mb-1">Username</div>
                            <div>{isFetchingUserProfile ? "Loading..." : resolvedName || subscription?.userId || "-"}</div>
                        </div>
                        <div className="col-md-6 mb-4">
                            <div className="font-weight-bold text-muted mb-1">Amount</div>
                            <div>{formatMaybeAmount(subscription?.currency || getCommercialPaperCurrency(commercialPaper || {}), getSubscriptionAmount(subscription))}</div>
                        </div>
                        <div className="col-md-6 mb-4">
                            <div className="font-weight-bold text-muted mb-1">Expected Returns</div>
                            <div>{formatMaybeAmount(subscription?.currency || getCommercialPaperCurrency(commercialPaper || {}), getSubscriptionExpectedReturns(subscription))}</div>
                        </div>
                        <div className="col-md-6 mb-4">
                            <div className="font-weight-bold text-muted mb-1">Value At Maturity</div>
                            <div>{formatMaybeAmount(subscription?.currency || getCommercialPaperCurrency(commercialPaper || {}), getSubscriptionValueAtMaturity(subscription))}</div>
                        </div>
                        <div className="col-md-6 mb-4">
                            <div className="font-weight-bold text-muted mb-1">Fees</div>
                            <div>{formatMaybeAmount(subscription?.currency || getCommercialPaperCurrency(commercialPaper || {}), getSubscriptionFees(subscription))}</div>
                        </div>
                        <div className="col-md-6 mb-4">
                            <div className="font-weight-bold text-muted mb-1">Interest Rate</div>
                            <div>{getSubscriptionInterestRate(subscription) ?? "-"}</div>
                        </div>
                        <div className="col-md-6 mb-4">
                            <div className="font-weight-bold text-muted mb-1">Status</div>
                            <div>{formatStatus(getSubscriptionStatus(subscription))}</div>
                        </div>
                        <div className="col-md-6 mb-4">
                            <div className="font-weight-bold text-muted mb-1">Subscription Date</div>
                            <div>{getSubscriptionDate(subscription) ? formatDate(getSubscriptionDate(subscription)) : "-"}</div>
                        </div>
                        <div className="col-md-6 mb-4">
                            <div className="font-weight-bold text-muted mb-1">Maturity Date</div>
                            <div>{getSubscriptionMaturityDate(subscription) ? formatDate(getSubscriptionMaturityDate(subscription)) : "-"}</div>
                        </div>
                        <div className="col-md-6 mb-4">
                            <div className="font-weight-bold text-muted mb-1">Payout Date</div>
                            <div>{getSubscriptionPayoutDate(subscription) ? formatDate(getSubscriptionPayoutDate(subscription)) : "-"}</div>
                        </div>
                        <div className="col-md-6 mb-4">
                            <div className="font-weight-bold text-muted mb-1">Transaction Ref</div>
                            <div>{getSubscriptionTransactionRef(subscription)}</div>
                        </div>
                        <div className="col-md-6 mb-4">
                            <div className="font-weight-bold text-muted mb-1">Account ID</div>
                            <div>{subscription?.accountId || "-"}</div>
                        </div>
                        <div className="col-md-6 mb-4">
                            <div className="font-weight-bold text-muted mb-1">Payment Method</div>
                            <div>{getSubscriptionPaymentMethod(subscription)}</div>
                        </div>
                        <div className="col-md-6 mb-4">
                            <div className="font-weight-bold text-muted mb-1">Days Accrued</div>
                            <div>{getSubscriptionDaysAccrued(subscription) ?? "-"}</div>
                        </div>
                        <div className="col-md-6 mb-4">
                            <div className="font-weight-bold text-muted mb-1">Days Left</div>
                            <div>{getSubscriptionDaysLeft(subscription) ?? "-"}</div>
                        </div>
                    </div>
                )}
            </Modal.Body>
            <Modal.Footer>
                <Button variant="light" onClick={onHide}>Close</Button>
            </Modal.Footer>
        </Modal>
    );
};

const CommercialPapersTable = ({ userGroup, commercialPapers, subscriptions, isFetchingCommercialPapers }) => {
    const [selectedCommercialPaper, setSelectedCommercialPaper] = useState(null);
    const [editingCommercialPaper, setEditingCommercialPaper] = useState(null);
    const [deletingCommercialPaper, setDeletingCommercialPaper] = useState(null);
    const [showCreateModal, setShowCreateModal] = useState(false);
    const [commercialPapersPage, setCommercialPapersPage] = useState(1);
    const [commercialPapersSize, setCommercialPapersSize] = useState(10);
    const [generateCommercialPapersReportCsv, { isLoading: isGeneratingCommercialPapersCsv }] = useGenerateCommercialPapersReportCsvMutation();
    const [generateCommercialPapersReportXlsx, { isLoading: isGeneratingCommercialPapersXlsx }] = useGenerateCommercialPapersReportXlsxMutation();
    const [deleteCommercialPaper, { isLoading: isDeletingCommercialPaper }] = useDeleteCommercialPaperMutation();

    const availableCommercialPapers = commercialPapers.filter((commercialPaper) => {
        const entryGroup = getCommercialPaperUserGroup(commercialPaper);
        return !entryGroup || entryGroup === userGroup;
    });
    return (
        <FilterGroup defaultFilters={{ commercialPaper: "", status: "" }}>
            <FilterGroup.Content>
                {({ filters }) => {
                    const filteredCommercialPapers = availableCommercialPapers.filter((commercialPaper) => {
                        const matchesPaper = !filters?.commercialPaper || getCommercialPaperId(commercialPaper) === filters.commercialPaper;
                        const matchesStatus = !filters?.status || (getCommercialPaperStatus(commercialPaper) || "").toLowerCase() === filters.status.toLowerCase();
                        return matchesPaper && matchesStatus;
                    });

                    const commercialPaperStats = filteredCommercialPapers.reduce((acc, commercialPaper) => {
                        const paperId = getCommercialPaperId(commercialPaper);
                        const relatedSubscriptions = subscriptions.filter((subscription) => getSubscriptionCommercialPaperId(subscription) === paperId);
                        const totalSubscribed = Number(getCommercialPaperTotalSubscribed(commercialPaper))
                            || relatedSubscriptions.reduce((sum, subscription) => sum + Number(getSubscriptionAmount(subscription) || 0), 0);
                        const investorCount = Number(commercialPaper?.numberOfInvestors || commercialPaper?.investorCount || commercialPaper?.subscribersCount || 0)
                            || new Set(relatedSubscriptions.map((subscription) => subscription?.userId || subscription?.email)).size;
                        acc[paperId] = { totalSubscribed, investorCount };
                        return acc;
                    }, {});

                    const commercialPapersTotalPages = Math.max(1, Math.ceil(filteredCommercialPapers.length / commercialPapersSize));
                    const currentCommercialPapersPage = Math.min(commercialPapersPage, commercialPapersTotalPages);
                    const visibleCommercialPapers = filteredCommercialPapers.slice(
                        (currentCommercialPapersPage - 1) * commercialPapersSize,
                        currentCommercialPapersPage * commercialPapersSize
                    );
                    const commercialPapersHasMore = currentCommercialPapersPage < commercialPapersTotalPages;

                    return (
                        <>
                            <div className="d-flex flex-wrap mb-4">
                                <FilterGroup.Filter name="commercialPaper" label="CP" width="280px">
                                    <CommercialPaperFilterOptions
                                        commercialPapers={availableCommercialPapers}
                                        title="All CPs"
                                        limit={10}
                                    />
                                </FilterGroup.Filter>
                                <div className="ml-3">
                                    <FilterGroup.Filter name="status" label="Status" width="200px">
                                        <FilterGroup.Option value="">All Statuses</FilterGroup.Option>
                                        {["active", "inactive", "matured"].map((status) => (
                                            <FilterGroup.Option key={status} value={status}>
                                                {ucFirst(status)}
                                            </FilterGroup.Option>
                                        ))}
                                    </FilterGroup.Filter>
                                </div>
                            </div>

                            <div className="row mb-4">
                                <div className="col-md-3 mb-4">
                                    <div className="card card-custom bg-light-primary border-0 shadow-none h-100 mb-0">
                                        <div className="card-body py-5">
                                            <div className="text-muted text-uppercase font-size-sm font-weight-bold mb-2">Commercial Papers</div>
                                            <div className="font-size-h2 font-weight-bolder text-dark">{filteredCommercialPapers.length}</div>
                                        </div>
                                    </div>
                                </div>
                                <div className="col-md-3 mb-4">
                                    <div className="card card-custom bg-light-info border-0 shadow-none h-100 mb-0">
                                        <div className="card-body py-5">
                                            <div className="text-muted text-uppercase font-size-sm font-weight-bold mb-2">Investors</div>
                                            <div className="font-size-h2 font-weight-bolder text-dark">
                                                {Object.values(commercialPaperStats).reduce((sum, entry) => sum + (entry?.investorCount || 0), 0)}
                                            </div>
                                        </div>
                                    </div>
                                </div>
                                <div className="col-md-3 mb-4" />
                                <div className="col-md-3 mb-4" />
                            </div>

                            <Card>
                                <CardHeader title="Commercial Papers Oversight">
                                    <CardHeaderToolbar>
                                        <div className="d-flex align-items-center">
                                            <button
                                                type="button"
                                                className="btn btn-light-primary mr-3"
                                                disabled={isGeneratingCommercialPapersCsv}
                                                onClick={() => {
                                                    const status = filters?.status || "";
                                                    generateCommercialPapersReportCsv({ status })
                                                        .unwrap()
                                                        .then((payload) => {
                                                            const message = payload?.message || payload?.data?.message || payload?.data || payload;
                                                            cogoToast.success(message || "Commercial papers report initialized. Open Reports to download.");
                                                        })
                                                        .catch(() => {});
                                                }}
                                            >
                                                {isGeneratingCommercialPapersCsv ? "Generating..." : "Generate CSV"}
                                            </button>
                                            <button
                                                type="button"
                                                className="btn btn-light-success mr-3"
                                                disabled={isGeneratingCommercialPapersXlsx}
                                                onClick={() => {
                                                    const status = filters?.status || "";
                                                    generateCommercialPapersReportXlsx({ status })
                                                        .unwrap()
                                                        .then((payload) => {
                                                            const message = payload?.message || payload?.data?.message || payload?.data || payload;
                                                            cogoToast.success(message || "Commercial papers report initialized. Open Reports to download.");
                                                        })
                                                        .catch(() => {});
                                                }}
                                            >
                                                {isGeneratingCommercialPapersXlsx ? "Generating..." : "Generate Excel"}
                                            </button>
                                            <Permission permission={Permissions.SUBSCRIPTIONS} permissionType={PermissionType.CREATE}>
                                                <button type="button" className="btn btn-primary" onClick={() => setShowCreateModal(true)}>
                                                    New Commercial Paper
                                                </button>
                                            </Permission>
                                        </div>
                                    </CardHeaderToolbar>
                                </CardHeader>
                                <CardBody>
                                    <div className="table-responsive">
                                        <table className="table table-head-custom table-head-bg table-borderless table-vertical-center">
                                            <thead>
                                                <tr className="text-left text-uppercase">
                                                    <th>Name</th>
                                                    <th>Issuer</th>
                                                    <th>Status</th>
                                                    <th>Duration</th>
                                                    <th>Interest</th>
                                                    <th>Yield</th>
                                                    <th>Currency</th>
                                                    <th>Min</th>
                                                    <th>Max</th>
                                                    <th>Subscription Start</th>
                                                    <th>Maturity</th>
                                                    <th>Total Subscribed</th>
                                                    <th>Investors</th>
                                                    <th>Updated</th>
                                                    <th></th>
                                                </tr>
                                            </thead>
                                            <tbody>
                                                {isFetchingCommercialPapers && (
                                                    <tr>
                                                        <td colSpan="15" className="text-muted">Loading commercial papers...</td>
                                                    </tr>
                                                )}
                                                {!isFetchingCommercialPapers && filteredCommercialPapers.length === 0 && (
                                                    <tr>
                                                        <td colSpan="15" className="text-muted">No commercial papers available.</td>
                                                    </tr>
                                                )}
                                                {visibleCommercialPapers.map((commercialPaper) => (
                                                    <tr key={getCommercialPaperId(commercialPaper)}>
                                                        <td className="font-weight-bolder">{getCommercialPaperName(commercialPaper)}</td>
                                                        <td>{getCommercialPaperIssuer(commercialPaper)}</td>
                                                        <td>{renderCommercialPaperStatus(getCommercialPaperStatus(commercialPaper))}</td>
                                                        <td>{getCommercialPaperTenor(commercialPaper)}</td>
                                                        <td>{commercialPaper?.interestRate ?? "-"}</td>
                                                        <td>{getCommercialPaperYield(commercialPaper) !== undefined && getCommercialPaperYield(commercialPaper) !== null ? `${getCommercialPaperYield(commercialPaper)}%` : "-"}</td>
                                                        <td>{getCommercialPaperCurrency(commercialPaper) || "-"}</td>
                                                        <td>{formatMaybeAmount(getCommercialPaperCurrency(commercialPaper), getCommercialPaperMinAmount(commercialPaper))}</td>
                                                        <td>{formatMaybeAmount(getCommercialPaperCurrency(commercialPaper), getCommercialPaperMaxAmount(commercialPaper))}</td>
                                                        <td>{getSubscriptionWindowStart(commercialPaper) ? formatDate(getSubscriptionWindowStart(commercialPaper)) : "-"}</td>
                                                        <td>{getCommercialPaperMaturityDate(commercialPaper) ? formatDate(getCommercialPaperMaturityDate(commercialPaper)) : "-"}</td>
                                                        <td>{formatMaybeAmount(getCommercialPaperCurrency(commercialPaper), commercialPaperStats[getCommercialPaperId(commercialPaper)]?.totalSubscribed)}</td>
                                                        <td>{commercialPaperStats[getCommercialPaperId(commercialPaper)]?.investorCount ?? 0}</td>
                                                        <td>{getCommercialPaperUpdatedAt(commercialPaper) ? formatDate(getCommercialPaperUpdatedAt(commercialPaper)) : "-"}</td>
                                                        <td className="text-right text-nowrap">
                                                            <div className="d-flex justify-content-end flex-nowrap">
                                                                <Button
                                                                    title="View Commercial Paper"
                                                                    className="btn btn-icon btn-light btn-hover-primary btn-sm bg-white mr-2"
                                                                    onClick={() => setSelectedCommercialPaper(commercialPaper)}
                                                                >
                                                                    <span className="svg-icon svg-icon-md svg-icon-primary">
                                                                        <ViewIcon />
                                                                    </span>
                                                                </Button>
                                                                <Permission permission={Permissions.SUBSCRIPTIONS} permissionType={PermissionType.UPDATE}>
                                                                    <Button
                                                                        title="Edit Commercial Paper"
                                                                        className="btn btn-icon btn-light btn-hover-primary btn-sm bg-white"
                                                                        onClick={() => setEditingCommercialPaper(commercialPaper)}
                                                                    >
                                                                        <span className="svg-icon svg-icon-md svg-icon-primary">
                                                                            <EditIcon />
                                                                        </span>
                                                                    </Button>
                                                                </Permission>
                                                                <Permission permission={Permissions.SUBSCRIPTIONS} permissionType={PermissionType.DELETE}>
                                                                    <Button
                                                                        title="Delete Commercial Paper"
                                                                        className="btn btn-icon btn-light btn-hover-danger btn-sm bg-white ml-2"
                                                                        onClick={() => setDeletingCommercialPaper(commercialPaper)}
                                                                    >
                                                                        <span className="svg-icon svg-icon-md svg-icon-danger">
                                                                            <TrashIcon />
                                                                        </span>
                                                                    </Button>
                                                                </Permission>
                                                            </div>
                                                        </td>
                                                    </tr>
                                                ))}
                                            </tbody>
                                        </table>
                                    </div>
                                    <div className="d-flex flex-wrap justify-content-between align-items-center mt-4">
                                        <div className="text-muted">
                                            Page {currentCommercialPapersPage} of {commercialPapersTotalPages}
                                        </div>
                                        <div className="d-flex align-items-center">
                                            <select
                                                className="form-control form-control-sm mr-3"
                                                value={commercialPapersSize}
                                                onChange={(event) => {
                                                    setCommercialPapersSize(Number(event.target.value));
                                                    setCommercialPapersPage(1);
                                                }}
                                            >
                                                {[10, 20, 50].map((size) => (
                                                    <option key={size} value={size}>{size} / page</option>
                                                ))}
                                            </select>
                                            <ul className="pagination pagination-sm mb-0">
                                                <li className={`page-item ${currentCommercialPapersPage <= 1 ? "disabled" : ""}`} style={{ marginRight: "0.5rem" }}>
                                                    <span
                                                        className="page-link"
                                                        style={{ cursor: currentCommercialPapersPage <= 1 ? "default" : "pointer" }}
                                                        onClick={() => currentCommercialPapersPage > 1 && setCommercialPapersPage((page) => Math.max(1, page - 1))}
                                                    >
                                                        Prev
                                                    </span>
                                                </li>
                                                <li className={`page-item ${commercialPapersHasMore ? "" : "disabled"}`}>
                                                    <span
                                                        className="page-link"
                                                        style={{ cursor: commercialPapersHasMore ? "pointer" : "default" }}
                                                        onClick={() => commercialPapersHasMore && setCommercialPapersPage((page) => page + 1)}
                                                    >
                                                        Next
                                                    </span>
                                                </li>
                                            </ul>
                                        </div>
                                    </div>
                                </CardBody>
                            </Card>

                            <Modal
                                show={!!deletingCommercialPaper}
                                onHide={() => setDeletingCommercialPaper(null)}
                                centered
                            >
                                <Modal.Header closeButton>
                                    <Modal.Title>Delete Commercial Paper</Modal.Title>
                                </Modal.Header>
                                <Modal.Body>
                                    <div className="text-dark-75">
                                        Delete <span className="font-weight-bolder">{getCommercialPaperName(deletingCommercialPaper)}</span>?
                                    </div>
                                    <div className="text-muted mt-2">
                                        This action cannot be undone.
                                    </div>
                                </Modal.Body>
                                <Modal.Footer>
                                    <Button variant="light" onClick={() => setDeletingCommercialPaper(null)} disabled={isDeletingCommercialPaper}>
                                        Cancel
                                    </Button>
                                    <Button
                                        variant="danger"
                                        onClick={() => {
                                            const cpId = getCommercialPaperId(deletingCommercialPaper);
                                            if (!cpId) return;
                                            deleteCommercialPaper(cpId)
                                                .unwrap()
                                                .then((payload) => {
                                                    const message = payload?.message || payload?.data?.message || payload?.data || payload;
                                                    cogoToast.success(message || "Commercial paper deleted.");
                                                    setDeletingCommercialPaper(null);
                                                })
                                                .catch(() => {});
                                        }}
                                        disabled={isDeletingCommercialPaper}
                                    >
                                        {isDeletingCommercialPaper ? "Deleting..." : "Delete"}
                                    </Button>
                                </Modal.Footer>
                            </Modal>

                            <Modal show={!!selectedCommercialPaper} onHide={() => setSelectedCommercialPaper(null)} size="lg" centered>
                                <Modal.Header closeButton>
                                    <Modal.Title id="contained-modal-title-vcenter">Commercial Paper Details</Modal.Title>
                                </Modal.Header>
                                <Modal.Body>
                                    {selectedCommercialPaper && (
                                        <div className="row">
                                            <div className="col-md-6 mb-4">
                                                <div className="font-weight-bold text-muted mb-1">Name</div>
                                                <div>{getCommercialPaperName(selectedCommercialPaper)}</div>
                                            </div>
                                            <div className="col-md-6 mb-4">
                                                <div className="font-weight-bold text-muted mb-1">Issuer</div>
                                                <div>{getCommercialPaperIssuer(selectedCommercialPaper)}</div>
                                            </div>
                                            <div className="col-md-12 mb-4">
                                                <div className="d-flex align-items-center">
                                                    {getCommercialPaperLogo(selectedCommercialPaper) ? (
                                                        <img
                                                            src={getCommercialPaperLogo(selectedCommercialPaper)}
                                                            alt="Commercial Paper Logo"
                                                            style={{ width: 48, height: 48, objectFit: "contain" }}
                                                        />
                                                    ) : null}
                                                    <div className="ml-3">
                                                        <div className="font-weight-bold text-muted mb-1">Provider Ref</div>
                                                        <div>{getCommercialPaperProviderRef(selectedCommercialPaper) || "-"}</div>
                                                    </div>
                                                    <div className="ml-5">
                                                        <div className="font-weight-bold text-muted mb-1">Product ID</div>
                                                        <div>{getCommercialPaperProductId(selectedCommercialPaper) ?? "-"}</div>
                                                    </div>
                                                </div>
                                            </div>
                                            <div className="col-md-6 mb-4">
                                                <div className="font-weight-bold text-muted mb-1">Status</div>
                                                <div>{renderCommercialPaperStatus(getCommercialPaperStatus(selectedCommercialPaper))}</div>
                                            </div>
                                            <div className="col-md-6 mb-4">
                                                <div className="font-weight-bold text-muted mb-1">Currency</div>
                                                <div>{getCommercialPaperCurrency(selectedCommercialPaper) || "-"}</div>
                                            </div>
                                            <div className="col-md-6 mb-4">
                                                <div className="font-weight-bold text-muted mb-1">Interest Rate</div>
                                                <div>{selectedCommercialPaper?.interestRate ?? "-"}</div>
                                            </div>
                                            <div className="col-md-6 mb-4">
                                                <div className="font-weight-bold text-muted mb-1">Yield</div>
                                                <div>{getCommercialPaperYield(selectedCommercialPaper) ?? "-"}</div>
                                            </div>
                                            <div className="col-md-6 mb-4">
                                                <div className="font-weight-bold text-muted mb-1">Duration</div>
                                                <div>{selectedCommercialPaper?.duration ?? selectedCommercialPaper?.tenor ?? "-"}</div>
                                            </div>
                                            <div className="col-md-6 mb-4">
                                                <div className="font-weight-bold text-muted mb-1">Maturity Date</div>
                                                <div>{selectedCommercialPaper?.maturityDate ? formatDate(selectedCommercialPaper.maturityDate) : "-"}</div>
                                            </div>
                                            <div className="col-md-6 mb-4">
                                                <div className="font-weight-bold text-muted mb-1">Min Amount</div>
                                                <div>{formatMaybeAmount(getCommercialPaperCurrency(selectedCommercialPaper), selectedCommercialPaper?.minAmount)}</div>
                                            </div>
                                            <div className="col-md-6 mb-4">
                                                <div className="font-weight-bold text-muted mb-1">Max Amount</div>
                                                <div>{formatMaybeAmount(getCommercialPaperCurrency(selectedCommercialPaper), selectedCommercialPaper?.maxAmount)}</div>
                                            </div>
                                            <div className="col-md-6 mb-4">
                                                <div className="font-weight-bold text-muted mb-1">Total Subscribed</div>
                                                <div>{formatMaybeAmount(getCommercialPaperCurrency(selectedCommercialPaper), getCommercialPaperTotalSubscribed(selectedCommercialPaper))}</div>
                                            </div>
                                            <div className="col-md-6 mb-4">
                                                <div className="font-weight-bold text-muted mb-1">Credit Rating</div>
                                                <div>{getCommercialPaperCreditRating(selectedCommercialPaper) || "-"}</div>
                                            </div>
                                            <div className="col-md-6 mb-4">
                                                <div className="font-weight-bold text-muted mb-1">Days Accrued</div>
                                                <div>{getCommercialPaperDaysAccrued(selectedCommercialPaper) ?? "-"}</div>
                                            </div>
                                            <div className="col-md-6 mb-4">
                                                <div className="font-weight-bold text-muted mb-1">Days Left</div>
                                                <div>{getCommercialPaperDaysLeft(selectedCommercialPaper) ?? "-"}</div>
                                            </div>
                                            <div className="col-md-6 mb-4">
                                                <div className="font-weight-bold text-muted mb-1">Created</div>
                                                <div>{selectedCommercialPaper?.created ? formatDate(selectedCommercialPaper.created) : "-"}</div>
                                            </div>
                                            <div className="col-md-6 mb-4">
                                                <div className="font-weight-bold text-muted mb-1">Updated</div>
                                                <div>{selectedCommercialPaper?.updated ? formatDate(selectedCommercialPaper.updated) : "-"}</div>
                                            </div>
                                            <div className="col-md-6 mb-4">
                                                <div className="font-weight-bold text-muted mb-1">Provider Created</div>
                                                <div>{getCommercialPaperSycamoreCreatedAt(selectedCommercialPaper) ? formatDate(getCommercialPaperSycamoreCreatedAt(selectedCommercialPaper)) : "-"}</div>
                                            </div>
                                            <div className="col-md-6 mb-4">
                                                <div className="font-weight-bold text-muted mb-1">Provider Updated</div>
                                                <div>{getCommercialPaperSycamoreUpdatedAt(selectedCommercialPaper) ? formatDate(getCommercialPaperSycamoreUpdatedAt(selectedCommercialPaper)) : "-"}</div>
                                            </div>
                                            <div className="col-md-12 mb-4">
                                                <div className="font-weight-bold text-muted mb-1">Description</div>
                                                <div>{stripHtml(getCommercialPaperDescription(selectedCommercialPaper)) || "-"}</div>
                                            </div>
                                        </div>
                                    )}
                                </Modal.Body>
                                <Modal.Footer>
                                    <Button variant="light" onClick={() => setSelectedCommercialPaper(null)}>Close</Button>
                                </Modal.Footer>
                            </Modal>

                            <CommercialPaperFormModal
                                show={showCreateModal}
                                onHide={() => setShowCreateModal(false)}
                                initialCommercialPaper={null}
                                userGroup={userGroup}
                            />
                            <CommercialPaperFormModal
                                show={!!editingCommercialPaper}
                                onHide={() => setEditingCommercialPaper(null)}
                                initialCommercialPaper={editingCommercialPaper}
                                userGroup={userGroup}
                            />
                        </>
                    );
                }}
            </FilterGroup.Content>
        </FilterGroup>
    );
};

const CommercialSubscriptionsTable = ({ userGroup, commercialPapers, subscriptions, isFetchingSubscriptions }) => {
    const [userFilter, setUserFilter] = useState("");
    const [selectedSubscription, setSelectedSubscription] = useState(null);
    const [subscriptionsPage, setSubscriptionsPage] = useState(1);
    const [subscriptionsSize, setSubscriptionsSize] = useState(10);
    const [generateSubscriptionsReportCsv, { isLoading: isGeneratingSubscriptionsCsv }] = useGenerateCommercialPaperSubscriptionsReportCsvMutation();
    const [generateSubscriptionsReportXlsx, { isLoading: isGeneratingSubscriptionsXlsx }] = useGenerateCommercialPaperSubscriptionsReportXlsxMutation();

    return (
        <FilterGroup defaultFilters={{ commercialPaper: "", subscriptionStatus: "", startDate: "", endDate: "" }}>
            <div className="d-flex flex-column mb-4">
                <div className="d-flex flex-wrap justify-content-between align-items-end mb-3">
                    <FilterGroup.DateRange />
                    <div className="ml-3 ml-sm-auto d-flex align-items-end">
                        <div>
                            <label className="font-weight-bolder d-block">User</label>
                            <Form.Control
                                type="text"
                                value={userFilter}
                                onChange={(event) => setUserFilter(event.target.value)}
                                placeholder="User ID, name or email"
                                style={{ minWidth: "240px" }}
                            />
                        </div>
                    </div>
                </div>

                <div className="d-flex flex-wrap align-items-end">
                    <FilterGroup.Filter name="commercialPaper" label="CP">
                        <CommercialPaperFilterOptions
                            commercialPapers={commercialPapers}
                            title="All CPs"
                            limit={10}
                        />
                    </FilterGroup.Filter>

                    <div className="ml-3">
                        <FilterGroup.Filter name="subscriptionStatus" label="Subscription Status" width="220px">
                            <FilterGroup.Option value="">All Statuses</FilterGroup.Option>
                            {[...new Set(subscriptions.map((subscription) => getSubscriptionStatus(subscription)).filter(Boolean))].map((status) => (
                                <FilterGroup.Option key={status} value={status}>
                                    {ucFirst(status)}
                                </FilterGroup.Option>
                            ))}
                        </FilterGroup.Filter>
                    </div>
                </div>
            </div>

            <FilterGroup.Content>
                {({ filters }) => {
                    const filteredSubscriptions = subscriptions.filter((subscription) => {
                        const entryGroup = getSubscriptionUserGroup(subscription);
                        const matchesGroup = !entryGroup || entryGroup === userGroup;
                        const matchesCommercialPaper = !filters?.commercialPaper || getSubscriptionCommercialPaperId(subscription) === filters.commercialPaper;
                        const matchesStatus = !filters?.subscriptionStatus || getSubscriptionStatus(subscription)?.toLowerCase() === filters.subscriptionStatus.toLowerCase();
                        const searchableContent = `${subscription?.userId || ""} ${subscription?.name || subscription?.username || ""} ${subscription?.email || ""}`.toLowerCase();
                        const matchesUser = !userFilter || searchableContent.includes(userFilter.toLowerCase());
                        const subscriptionDate = toDateValue(getSubscriptionDate(subscription));
                        const startDate = toDateValue(filters?.startDate);
                        const endDate = toDateValue(filters?.endDate);
                        const matchesStartDate = !startDate || (subscriptionDate && subscriptionDate >= startDate);
                        const matchesEndDate = !endDate || (subscriptionDate && subscriptionDate <= endDate);
                        return matchesGroup && matchesCommercialPaper && matchesStatus && matchesUser && matchesStartDate && matchesEndDate;
                    });

                    const subscriptionsTotalPages = Math.max(1, Math.ceil(filteredSubscriptions.length / subscriptionsSize));
                    const currentSubscriptionsPage = Math.min(subscriptionsPage, subscriptionsTotalPages);
                    const visibleSubscriptions = filteredSubscriptions.slice(
                        (currentSubscriptionsPage - 1) * subscriptionsSize,
                        currentSubscriptionsPage * subscriptionsSize
                    );
                    const subscriptionsHasMore = currentSubscriptionsPage < subscriptionsTotalPages;
                    const downloadCsvReport = () => {
                        const status = filters?.subscriptionStatus || "";
                        generateSubscriptionsReportCsv({ status, cpId: filters?.commercialPaper || "", userId: userFilter || "" })
                            .unwrap()
                            .then((payload) => {
                                const message = payload?.message || payload?.data?.message || payload?.data || payload;
                                cogoToast.success(message || "Commercial paper subscriptions report initialized. Open Reports to download.");
                            })
                            .catch(() => {});
                    };
                    const downloadXlsxReport = () => {
                        const status = filters?.subscriptionStatus || "";
                        generateSubscriptionsReportXlsx({ status, cpId: filters?.commercialPaper || "", userId: userFilter || "" })
                            .unwrap()
                            .then((payload) => {
                                const message = payload?.message || payload?.data?.message || payload?.data || payload;
                                cogoToast.success(message || "Commercial paper subscriptions report initialized. Open Reports to download.");
                            })
                            .catch(() => {});
                    };

                    return (
                        <>
                            <Card>
                                <CardHeader title="Commercial Paper Subscriptions">
                                    <CardHeaderToolbar>
                                        <div className="d-flex align-items-center">
                                            <button type="button" className="btn btn-light-primary mr-3" disabled={isGeneratingSubscriptionsCsv} onClick={downloadCsvReport}>
                                                {isGeneratingSubscriptionsCsv ? "Generating..." : "Generate CSV"}
                                            </button>
                                                <button type="button" className="btn btn-light-success" disabled={isGeneratingSubscriptionsXlsx} onClick={downloadXlsxReport}>
                                                    {isGeneratingSubscriptionsXlsx ? "Generating..." : "Generate Excel"}
                                            </button>
                                        </div>
                                    </CardHeaderToolbar>
                                </CardHeader>
                                <CardBody>
                                    <div className="table-responsive">
                                        <table className="table table-head-custom table-head-bg table-borderless table-vertical-center">
                                            <thead>
                                                <tr className="text-left text-uppercase">
                                                    <th>CP</th>
                                                    <th>Name</th>
                                                    <th>Amount</th>
                                                    <th>Expected Returns</th>
                                                    <th>Value At Maturity</th>
                                                    <th>Transaction Ref</th>
                                                    <th>Subscription Date</th>
                                                    <th>Status</th>
                                                    <th>Maturity Date</th>
                                                    <th></th>
                                                </tr>
                                            </thead>
                                            <tbody>
                                                {isFetchingSubscriptions && (
                                                    <tr>
                                                        <td colSpan="10" className="text-muted">Loading subscriptions...</td>
                                                    </tr>
                                                )}
                                                {!isFetchingSubscriptions && filteredSubscriptions.length === 0 && (
                                                    <tr>
                                                        <td colSpan="10" className="text-muted">No subscriptions match the selected filters.</td>
                                                    </tr>
                                                )}
                                                {visibleSubscriptions.map((subscription, index) => {
                                                    const relatedCommercialPaper = commercialPapers.find((commercialPaper) => getCommercialPaperId(commercialPaper) === getSubscriptionCommercialPaperId(subscription));
                                                    return (
                                                        <tr key={`${subscription?.id || subscription?.referenceId || subscription?.userId}-${index}`}>
                                                            <td>{getSubscriptionCommercialPaperName(subscription, relatedCommercialPaper)}</td>
                                                            <td>{formatUsername(subscription?.userId, subscription?.name || subscription?.username, null, null, "commercial-paper-subscriptions")}</td>
                                                            <td>{formatMaybeAmount(subscription?.currency || getCommercialPaperCurrency(relatedCommercialPaper || {}), getSubscriptionAmount(subscription))}</td>
                                                            <td>{formatMaybeAmount(subscription?.currency || getCommercialPaperCurrency(relatedCommercialPaper || {}), getSubscriptionExpectedReturns(subscription))}</td>
                                                            <td>{formatMaybeAmount(subscription?.currency || getCommercialPaperCurrency(relatedCommercialPaper || {}), getSubscriptionValueAtMaturity(subscription))}</td>
                                                            <td>{getSubscriptionTransactionRef(subscription)}</td>
                                                            <td>{getSubscriptionDate(subscription) ? formatDate(getSubscriptionDate(subscription)) : "-"}</td>
                                                            <td>{formatStatus(getSubscriptionStatus(subscription))}</td>
                                                            <td>{getSubscriptionMaturityDate(subscription) ? formatDate(getSubscriptionMaturityDate(subscription)) : "-"}</td>
                                                            <td className="text-right">
                                                                <Button
                                                                    title="View Subscription"
                                                                    className="btn btn-icon btn-light btn-hover-primary btn-sm bg-white"
                                                                    onClick={() => setSelectedSubscription(subscription)}
                                                                >
                                                                    <span className="svg-icon svg-icon-md svg-icon-primary">
                                                                        <ViewIcon />
                                                                    </span>
                                                                </Button>
                                                            </td>
                                                        </tr>
                                                    );
                                                })}
                                            </tbody>
                                        </table>
                                    </div>
                                    <div className="d-flex flex-wrap justify-content-between align-items-center mt-4">
                                        <div className="text-muted">
                                            Page {currentSubscriptionsPage} of {subscriptionsTotalPages}
                                        </div>
                                        <div className="d-flex align-items-center">
                                            <select
                                                className="form-control form-control-sm mr-3"
                                                value={subscriptionsSize}
                                                onChange={(event) => {
                                                    setSubscriptionsSize(Number(event.target.value));
                                                    setSubscriptionsPage(1);
                                                }}
                                            >
                                                {[10, 20, 50].map((size) => (
                                                    <option key={size} value={size}>{size} / page</option>
                                                ))}
                                            </select>
                                            <ul className="pagination pagination-sm mb-0">
                                                <li className={`page-item ${currentSubscriptionsPage <= 1 ? "disabled" : ""}`} style={{ marginRight: "0.5rem" }}>
                                                    <span
                                                        className="page-link"
                                                        style={{ cursor: currentSubscriptionsPage <= 1 ? "default" : "pointer" }}
                                                        onClick={() => currentSubscriptionsPage > 1 && setSubscriptionsPage((page) => Math.max(1, page - 1))}
                                                    >
                                                        Prev
                                                    </span>
                                                </li>
                                                <li className={`page-item ${subscriptionsHasMore ? "" : "disabled"}`}>
                                                    <span
                                                        className="page-link"
                                                        style={{ cursor: subscriptionsHasMore ? "pointer" : "default" }}
                                                        onClick={() => subscriptionsHasMore && setSubscriptionsPage((page) => page + 1)}
                                                    >
                                                        Next
                                                    </span>
                                                </li>
                                            </ul>
                                        </div>
                                    </div>
                                </CardBody>
                            </Card>

                            <SubscriptionDetailsModal
                                subscription={selectedSubscription}
                                commercialPaper={commercialPapers.find((commercialPaper) => getCommercialPaperId(commercialPaper) === getSubscriptionCommercialPaperId(selectedSubscription || {}))}
                                onHide={() => setSelectedSubscription(null)}
                            />
                        </>
                    );
                }}
            </FilterGroup.Content>
        </FilterGroup>
    );
};

export const CommercialPapersManagement = ({
    match: {
        params: { userGroup },
    },
}) => {
    const { data: commercialPapersResponse, isFetching: isFetchingCommercialPapers } = useGetCommercialPapersQuery();
    const { data: commercialPaperSubscriptionsResponse, isFetching: isFetchingSubscriptions } = useGetCommercialPaperSubscriptionsQuery();

    const commercialPapers = normalizeArray(commercialPapersResponse?.data ?? commercialPapersResponse);
    const subscriptions = normalizeArray(commercialPaperSubscriptionsResponse?.data ?? commercialPaperSubscriptionsResponse);

    return (
        <>
            <SubHeader>
                <SubHeader.Nav>
                    <SubHeader.NavItem label={"Commercial Papers"} to={`/commercial-papers/${userGroup}/all`} />
                    <SubHeader.NavItem label={"Commercial Subscriptions"} to={`/commercial-papers/${userGroup}/subscriptions`} />
                </SubHeader.Nav>
            </SubHeader>
            <div className="py-4 px-6">
                <Switch>
                    <Redirect exact from="/commercial-papers/:userGroup" to="/commercial-papers/:userGroup/all" />

                    <ContentRoute path="/commercial-papers/:userGroup/all" permission={Permissions.SUBSCRIPTIONS}>
                        <CommercialPapersTable
                            userGroup={userGroup}
                            commercialPapers={commercialPapers}
                            subscriptions={subscriptions}
                            isFetchingCommercialPapers={isFetchingCommercialPapers}
                        />
                    </ContentRoute>

                    <ContentRoute path="/commercial-papers/:userGroup/subscriptions" permission={Permissions.SUBSCRIPTIONS}>
                        <CommercialSubscriptionsTable
                            userGroup={userGroup}
                            commercialPapers={commercialPapers}
                            subscriptions={subscriptions}
                            isFetchingSubscriptions={isFetchingSubscriptions}
                        />
                    </ContentRoute>
                </Switch>
            </div>
        </>
    );
};
